import React, { useState, useEffect, useMemo } from 'react';
import {
    Box,
    Typography,
    TextField,
    Checkbox,
    FormControlLabel,
    InputAdornment,
    Divider,
    Stack,
    Chip,
    Popover,
    Grid,
    Button
} from '@mui/material';
import {
    Clear as ClearIcon,
    KeyboardArrowDown as ChevronDownIcon,
    Search as SearchIcon,
    Close as CloseIcon
} from '@mui/icons-material';
import WidgetWraper from 'components/Common/WidgetWraper';
import { MUTED_TEXT, SKY_BLUE } from './constants';
import { cardTitleSx } from './styles';

type FilterOption = string | {
    label: string;
    value: string;
};

/**
 * Chip-style filter group for Advanced Filters
 */
export const AdvancedFilterChipGroup: React.FC<{
    label: string;
    selectedValues: string[];
    onValuesChange: (vals: string[]) => void;
    allOptions: FilterOption[];
    disabled?: boolean;
}> = ({ label, selectedValues, onValuesChange, allOptions, disabled = false }) => {
    const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
    const [search, setSearch] = useState('');

    const normalizedOptions = allOptions.map((option) =>
        typeof option === 'string'
            ? { label: option, value: option }
            : option
    );

    const allOption = normalizedOptions.find(option => option.value === 'all');
    const optionsWithoutAll = normalizedOptions.filter(option => option.value !== 'all');

    const effectiveSelectedValues = selectedValues.includes(allOption?.value || 'all')
        ? optionsWithoutAll.map(option => option.value)
        : selectedValues;

    const getOptionLabel = (value: string) => {
        return normalizedOptions.find(option => option.value === value)?.label || value;
    };

    const handleClick = (event: React.MouseEvent<HTMLElement>) => {
        if (disabled) return;
        setAnchorEl(event.currentTarget);
    };

    const handleClose = () => {
        setAnchorEl(null);
    };

    const open = Boolean(anchorEl);

    const handleToggleValue = (val: string) => {
        if (disabled) return;

        if (val === 'ALL') {
            if (allOption) {
                if (selectedValues.includes(allOption.value)) {
                    onValuesChange([]);
                } else {
                    onValuesChange([allOption.value]);
                }
            } else if (selectedValues.length === normalizedOptions.length) {
                onValuesChange([]);
            } else {
                onValuesChange(normalizedOptions.map(option => option.value));
            }
            return;
        }

        if (allOption && val === allOption.value) {
            if (selectedValues.includes(allOption.value)) {
                onValuesChange([]);
            } else {
                onValuesChange([allOption.value]);
            }
            return;
        }

        let currentSelectedValues = selectedValues.includes(allOption?.value || 'all')
            ? optionsWithoutAll.map(option => option.value)
            : selectedValues;

        let newVals = currentSelectedValues.includes(val)
            ? currentSelectedValues.filter(v => v !== val)
            : [...currentSelectedValues.filter(v => v !== allOption?.value), val];

        if (allOption && newVals.length === optionsWithoutAll.length && optionsWithoutAll.every(option => newVals.includes(option.value))) {
            onValuesChange([allOption.value]);
            return;
        }

        onValuesChange(newVals);
    };

    const filteredOptions = optionsWithoutAll.filter(o => o.label.toLowerCase().includes(search.toLowerCase()));
    const isAllChecked = allOption
        ? selectedValues.includes(allOption.value) || optionsWithoutAll.every(option => selectedValues.includes(option.value))
        : selectedValues.length === normalizedOptions.length && normalizedOptions.length > 0;

    return (
        <>
            <Box
                sx={{
                    border: '1px solid #E0E4EC',
                    borderRadius: '4px',
                    p: '4px 12px',
                    position: 'relative',
                    minWidth: '150px',
                    backgroundColor: disabled ? '#F3F6FA' : '#fff',
                    cursor: disabled ? 'not-allowed' : 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    opacity: disabled ? 0.7 : 1
                }}
                onClick={handleClick}
            >
                <Box sx={{ position: 'absolute', top: '-10px', left: '10px', backgroundColor: disabled ? '#F3F6FA' : '#fff', px: 0.5 }}>
                    <Typography sx={{ fontSize: '11px', color: '#71758F' }}>{label}</Typography>
                </Box>

                <Stack direction="row" spacing={1} sx={{ overflow: 'hidden', flexWrap: 'nowrap', py: 1 }}>
                    {selectedValues.length === 0 ? (
                        <Typography sx={{ color: MUTED_TEXT, fontSize: '13px' }}>{label}</Typography>
                    ) : (
                        (selectedValues.includes(allOption?.value || 'all') && allOption ? [allOption.value] : selectedValues).map(v => (
                            <Chip
                                key={v}
                                label={getOptionLabel(v)}
                                size="small"
                                deleteIcon={<CloseIcon />}
                                onDelete={(e) => {
                                    e.stopPropagation();
                                    handleToggleValue(v);
                                }}
                                sx={{
                                    height: '24px',
                                    backgroundColor: '#39ACE733 !important',
                                    color: '#000000 !important',
                                    '& .MuiChip-deleteIcon': {
                                        fontSize: '14px',
                                        color: '#000000 !important',
                                    }
                                }}
                            />
                        ))
                    )}
                </Stack>

                <ChevronDownIcon sx={{ color: disabled ? '#AAB5C3' : SKY_BLUE }} />
            </Box>

            <Popover
                open={open}
                anchorEl={anchorEl}
                onClose={handleClose}
                anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'left',
                }}
                transformOrigin={{
                    vertical: 'top',
                    horizontal: 'left',
                }}
                slotProps={{
                    paper: {
                        sx: {
                            mt: 0,
                            width: anchorEl ? (anchorEl).offsetWidth : 220,
                            borderRadius: '0 0 8px 8px',
                            boxShadow: '0px 8px 16px rgba(0,0,0,0.08)',
                            border: '1px solid #E0E4EC',
                            borderTop: 'none',
                            backgroundColor: '#ffffff',
                            overflow: 'hidden'
                        }
                    }
                }}
            >
                <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                    {/* Search Area */}
                    <Box sx={{
                        backgroundColor: '#ffffff',
                        px: 1.5,
                        py: 0.5,
                        borderBottom: '1px solid #F0F2F5',
                        display: 'flex',
                        alignItems: 'center'
                    }}>
                        <TextField
                            fullWidth
                            size="small"
                            variant="standard"
                            placeholder={`Search ${label}`}
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                            onClick={(e) => e.stopPropagation()}
                            autoComplete="off"
                            InputProps={{
                                disableUnderline: true,
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <SearchIcon sx={{ fontSize: '20px', color: '#71758F' }} />
                                    </InputAdornment>
                                ),
                            }}
                            inputProps={{
                                style: {
                                    color: '#000000 !important',
                                    WebkitTextFillColor: '#000000 !important',
                                    caretColor: '#000000 !important',
                                }
                            }}
                            sx={{
                                backgroundColor: '#ffffff',
                                '& .MuiInputBase-root': {
                                    height: '40px',
                                    fontSize: '14px',
                                    fontFamily: 'Montserrat',
                                    color: '#000000 !important',
                                    backgroundColor: '#ffffff',
                                },
                                '& .MuiInputBase-input': {
                                    color: '#000000 !important',
                                    WebkitTextFillColor: '#000000 !important',
                                    caretColor: '#000000 !important',
                                    backgroundColor: '#ffffff !important',
                                    opacity: '1 !important',
                                },
                                '& .MuiInputBase-input::placeholder': {
                                    color: '#71758F !important',
                                    WebkitTextFillColor: '#71758F !important',
                                    opacity: '0.8 !important',
                                },
                            }}
                        />
                    </Box>

                    {/* Options Area */}
                    <Box sx={{ p: 1, maxHeight: '250px', overflowY: 'auto', backgroundColor: '#FBFDFF' }}>
                        <Stack spacing={0.2}>
                            <Box sx={{
                                '&:hover': { backgroundColor: '#F0F7FF' },
                                borderRadius: '4px'
                            }}>
                                <FormControlLabel
                                    control={
                                        <Checkbox
                                            size="small"
                                            checked={isAllChecked}
                                            onChange={() => handleToggleValue('ALL')}
                                            sx={{
                                                color: '#BDC5D0',
                                                '&.Mui-checked': { color: SKY_BLUE }
                                            }}
                                        />
                                    }
                                    label={<Typography sx={{ fontSize: '13px', fontFamily: 'Montserrat', color: '#444' }}>{allOption?.label || 'ALL'}</Typography>}
                                    sx={{ m: 0, width: '100%', py: 0.5 }}
                                />
                            </Box>
                            {filteredOptions.map(opt => (
                                <Box key={opt.value} sx={{
                                    '&:hover': { backgroundColor: '#F0F7FF' },
                                    borderRadius: '4px'
                                }}>
                                    <FormControlLabel
                                        control={
                                            <Checkbox
                                                size="small"
                                                checked={effectiveSelectedValues.includes(opt.value)}
                                                onChange={() => handleToggleValue(opt.value)}
                                                sx={{
                                                    color: '#BDC5D0',
                                                    '&.Mui-checked': { color: SKY_BLUE }
                                                }}
                                            />
                                        }
                                        label={<Typography sx={{ fontSize: '13px', fontFamily: 'Montserrat', color: '#444' }}>{opt.label}</Typography>}
                                        sx={{ m: 0, width: '100%', py: 0.5 }}
                                    />
                                </Box>
                            ))}
                        </Stack>
                    </Box>
                </Box>
            </Popover>
        </>
    );
};

interface AdvancedFiltersCardProps {
    onApply?: (filters: any) => void;
    onClear?: () => void;
    title?: string;
    sx?: any;
    excludeKeys?: string[];
    filterOptions: Record<string, FilterOption[]>;
    initialFilters?: Record<string, string[]>;
    disabledKeys?: string[];
}

/**
 * Advanced Filters Section
 */
export const AdvancedFiltersCard: React.FC<AdvancedFiltersCardProps> = ({
    onApply,
    onClear,
    title,
    sx,
    excludeKeys = [],
    filterOptions,
    initialFilters,
    disabledKeys = []
}) => {
    const initialFiltersKey = useMemo(() => JSON.stringify(initialFilters || {}), [initialFilters]);
    const [filters, setFilters] = useState<Record<string, string[]>>(initialFilters || {});

    useEffect(() => {
        setFilters(initialFilters || {});
    }, [initialFiltersKey]);

    const options = filterOptions;

    const handleClearAll = () => {
        const clearedFilters = { ...filters };
        Object.keys(clearedFilters).forEach(key => {
            clearedFilters[key] = [];
        });
        setFilters(clearedFilters);
        onClear?.();
    };

    const visibleFilterKeys = Object.keys(filters).filter(key => !excludeKeys.includes(key));
    const gridMd = visibleFilterKeys.length > 0 ? (12 / visibleFilterKeys.length > 2 ? 12 / visibleFilterKeys.length : 2.4) : 2.4;

    return (
        <WidgetWraper sx={{ backgroundColor: '#fff', ...sx }}>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                <Typography sx={cardTitleSx}>{title || 'Advanced Filters'}</Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 3 }}>
                    <Typography
                        sx={{ fontSize: '13px', color: SKY_BLUE, fontWeight: 600, cursor: 'pointer', display: 'flex', alignItems: 'center' }}
                        onClick={handleClearAll}
                    >
                        <ClearIcon sx={{ fontSize: '16px', mr: 0.5 }} /> CLEAR ALL
                    </Typography>
                    {onApply && (
                        <Button
                            variant="outlined"
                            size="small"
                            onClick={() => onApply(filters)}
                            sx={{
                                borderColor: SKY_BLUE,
                                color: SKY_BLUE,
                                textTransform: 'uppercase',
                                px: 4,
                                borderRadius: '4px',
                                fontWeight: 600,
                                '&:hover': { borderColor: SKY_BLUE, backgroundColor: 'rgba(57, 172, 231, 0.04)' }
                            }}
                        >
                            Apply
                        </Button>
                    )}
                </Box>
            </Box>
            <Divider sx={{ mx: 2, mb: 2 }} />
            <Grid container spacing={2}>
                {visibleFilterKeys.map((key) => (
                    <Grid item xs={12} sm={6} md={gridMd} key={key}>
                        <AdvancedFilterChipGroup
                            label={key}
                            selectedValues={filters[key] || []}
                            allOptions={(options[key] || []).filter((option) =>
                                typeof option === 'string' ? option !== 'ALL' : option.value !== 'ALL'
                            )}
                            disabled={disabledKeys.includes(key)}
                            onValuesChange={(vals) => setFilters((prev) => ({ ...prev, [key]: vals }))}
                        />
                    </Grid>
                ))}
            </Grid>
        </WidgetWraper>
    );
};

export default AdvancedFiltersCard;